import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listSuppliers,
  getSupplier,
  updateSupplier,
  createSupplier,
} from '@/services/suppliersApi'
import type { ListSuppliersParams, UpdateSupplierData, CreateSupplierData } from '@/types'

// Hook for fetching supplier list with pagination
export function useSuppliers(params: ListSuppliersParams) {
  return useQuery({
    queryKey: ['suppliers', params],
    queryFn: () => listSuppliers(params),
    staleTime: 30000, // 30 seconds
    enabled: !!params.business_id,
  })
}

// Hook for fetching single supplier
export function useSupplier(id: string | null) {
  return useQuery({
    queryKey: ['supplier', id],
    queryFn: () => getSupplier(id!),
    enabled: !!id,
  })
}

// Hook for updating supplier
export function useUpdateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierData }) =>
      updateSupplier(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific supplier query
      queryClient.invalidateQueries({ queryKey: ['supplier', id] })
      // Invalidate supplier list to reflect changes
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}


// Hook for creating supplier
export function useCreateSupplier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateSupplierData) => createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}
